const sortByZ = (elements) =>
  [...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

const applyOrder = (elements, ordered) => {
  const orderMap = new Map();
  ordered.forEach((el, index) => {
    orderMap.set(el.id, index + 1);
  });
  return elements.map((el) => (
    orderMap.has(el.id)
      ? { ...el, zIndex: orderMap.get(el.id) }
      : el
  ));
};

const moveForwardOne = (ordered, selectedSet) => {
  const next = [...ordered];
  for (let index = next.length - 2; index >= 0; index -= 1) {
    const current = next[index];
    const above = next[index + 1];
    if (selectedSet.has(current.id) && !selectedSet.has(above.id)) {
      next[index] = above;
      next[index + 1] = current;
    }
  }
  return next;
};

const moveBackwardOne = (ordered, selectedSet) => {
  const next = [...ordered];
  for (let index = 1; index < next.length; index += 1) {
    const current = next[index];
    const below = next[index - 1];
    if (selectedSet.has(current.id) && !selectedSet.has(below.id)) {
      next[index] = below;
      next[index - 1] = current;
    }
  }
  return next;
};

const moveToFront = (ordered, selectedSet) => {
  const selected = ordered.filter((el) => selectedSet.has(el.id));
  const remaining = ordered.filter((el) => !selectedSet.has(el.id));
  return [...remaining, ...selected];
};

const moveToBack = (ordered, selectedSet) => {
  const selected = ordered.filter((el) => selectedSet.has(el.id));
  const remaining = ordered.filter((el) => !selectedSet.has(el.id));
  return [...selected, ...remaining];
};

export const normalizeZIndices = (elements) => {
  const sorted = sortByZ(elements);
  return applyOrder(elements, sorted);
};

export const bringForward = (elements, selectedIds) => {
  if (!selectedIds.length) return elements;
  const ordered = sortByZ(elements);
  const selectedSet = new Set(selectedIds);
  const moved = moveForwardOne(ordered, selectedSet);
  return applyOrder(elements, moved);
};

export const sendBackward = (elements, selectedIds) => {
  if (!selectedIds.length) return elements;
  const ordered = sortByZ(elements);
  const selectedSet = new Set(selectedIds);
  const moved = moveBackwardOne(ordered, selectedSet);
  return applyOrder(elements, moved);
};

export const bringToFront = (elements, selectedIds) => {
  if (!selectedIds.length) return elements;
  const ordered = sortByZ(elements);
  const selectedSet = new Set(selectedIds);
  const moved = moveToFront(ordered, selectedSet);
  return applyOrder(elements, moved);
};

export const sendToBack = (elements, selectedIds) => {
  if (!selectedIds.length) return elements;
  const ordered = sortByZ(elements);
  const selectedSet = new Set(selectedIds);
  const moved = moveToBack(ordered, selectedSet);
  return applyOrder(elements, moved);
};

const getBounds = (elements) => {
  const xs = elements.map((el) => el.x);
  const ys = elements.map((el) => el.y);
  const rights = elements.map((el) => el.x + el.w);
  const bottoms = elements.map((el) => el.y + el.h);
  return {
    left: Math.min(...xs),
    top: Math.min(...ys),
    right: Math.max(...rights),
    bottom: Math.max(...bottoms),
    centerX: (Math.min(...xs) + Math.max(...rights)) / 2,
    centerY: (Math.min(...ys) + Math.max(...bottoms)) / 2,
  };
};

export const alignElements = (
  elements,
  selectedIds,
  align,
  { artboardWidth, artboardHeight, alignToPage = false },
) => {
  const selected = elements.filter((el) => selectedIds.includes(el.id));
  if (!selected.length) return elements;
  const bounds = alignToPage
    ? {
        left: 0,
        top: 0,
        right: artboardWidth,
        bottom: artboardHeight,
        centerX: artboardWidth / 2,
        centerY: artboardHeight / 2,
      }
    : getBounds(selected);
  return elements.map((el) => {
    if (!selectedIds.includes(el.id)) return el;
    if (align === 'left') return { ...el, x: bounds.left };
    if (align === 'right') return { ...el, x: bounds.right - el.w };
    if (align === 'center') return { ...el, x: bounds.centerX - el.w / 2 };
    if (align === 'top') return { ...el, y: bounds.top };
    if (align === 'bottom') return { ...el, y: bounds.bottom - el.h };
    if (align === 'middle') return { ...el, y: bounds.centerY - el.h / 2 };
    return el;
  });
};

export const distributeElements = (elements, selectedIds, axis) => {
  const selected = elements.filter((el) => selectedIds.includes(el.id));
  if (selected.length < 3) return elements;
  const sorted = [...selected].sort((a, b) =>
    axis === 'horizontal' ? a.x - b.x : a.y - b.y,
  );
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const totalSpan =
    axis === 'horizontal'
      ? last.x + last.w - first.x
      : last.y + last.h - first.y;
  const totalSize = sorted.reduce(
    (acc, el) => acc + (axis === 'horizontal' ? el.w : el.h),
    0,
  );
  const gap = (totalSpan - totalSize) / (sorted.length - 1 || 1);
  const positions = new Map();
  let cursor = axis === 'horizontal' ? first.x : first.y;
  for (let index = 1; index < sorted.length; index += 1) {
    const prev = sorted[index - 1];
    cursor += (axis === 'horizontal' ? prev.w : prev.h) + gap;
    positions.set(sorted[index].id, cursor);
  }
  return elements.map((el) => {
    if (!selectedIds.includes(el.id)) return el;
    if (!positions.has(el.id)) return el;
    if (axis === 'horizontal') {
      return { ...el, x: positions.get(el.id) };
    }
    return { ...el, y: positions.get(el.id) };
  });
};
